import { useState, useMemo, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'orcai_sinapi_orcamento';

function loadFromStorage() {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : { items: [], bdi: 25 };
  } catch {
    return { items: [], bdi: 25 };
  }
}

function saveToStorage(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {}
}

export default function useOrcamentoSinapi() {
  const [state, setState] = useState(loadFromStorage);

  useEffect(() => {
    saveToStorage(state);
  }, [state]);

  const addItem = useCallback((item) => {
    setState((prev) => {
      const existing = prev.items.find((i) => i.codigo === item.codigo);
      if (existing) {
        return {
          ...prev,
          items: prev.items.map((i) =>
            i.codigo === item.codigo ? { ...i, quantidade: i.quantidade + (item.quantidade || 1) } : i
          ),
        };
      }
      return {
        ...prev,
        items: [...prev.items, {
          codigo: item.codigo,
          descricao: item.descricao,
          unidade: item.unidade,
          preco: item.preco,
          quantidade: item.quantidade || 1,
        }],
      };
    });
  }, []);

  const removeItem = useCallback((codigo) => {
    setState((prev) => ({
      ...prev,
      items: prev.items.filter((i) => i.codigo !== codigo),
    }));
  }, []);

  const updateQuantidade = useCallback((codigo, quantidade) => {
    setState((prev) => ({
      ...prev,
      items: prev.items.map((i) =>
        i.codigo === codigo ? { ...i, quantidade: Math.max(0.01, quantidade) } : i
      ),
    }));
  }, []);

  const setBdi = useCallback((bdi) => {
    setState((prev) => ({ ...prev, bdi: Math.min(50, Math.max(0, bdi)) }));
  }, []);

  const clearAll = useCallback(() => {
    setState({ items: [], bdi: 25 });
  }, []);

  const subtotal = useMemo(() => {
    return state.items.reduce((sum, item) => {
      return sum + (item.preco || 0) * item.quantidade;
    }, 0);
  }, [state.items]);

  const bdiValor = useMemo(() => subtotal * (state.bdi / 100), [subtotal, state.bdi]);
  const total = useMemo(() => subtotal + bdiValor, [subtotal, bdiValor]);

  return {
    items: state.items,
    bdi: state.bdi,
    addItem,
    removeItem,
    updateQuantidade,
    setBdi,
    clearAll,
    subtotal,
    bdiValor,
    total,
  };
}
